import { startOfDay, subDays } from 'date-fns';

/**
 * Shared "created date" list-filter model for the Workflow and Batches lists: a set of relative
 * presets plus a custom range. Kept as a pure module so the date-bound computation is unit-testable
 * and identical on both screens.
 */
export type DatePreset = '' | 'last7' | 'last14' | 'last30' | 'last90' | 'custom';

export const DATE_PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: '', label: 'Any date' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last14', label: 'Last 14 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

const PRESET_DAYS: Record<string, number> = {
  last7: 7,
  last14: 14,
  last30: 30,
  last90: 90,
};

/**
 * Resolve a preset (+ optional custom range) into concrete from/to bounds.
 *
 * Presets are relative to "now", so "Last 7 days" is always fresh — `from` is the start of the day
 * N days ago and `to` is null (open-ended up to now). "custom" passes the user's dates through
 * unchanged; "" (Any date) yields no bounds.
 */
export function computeDateRange(
  preset: DatePreset,
  customFrom: Date | null,
  customTo: Date | null,
): { from: Date | null; to: Date | null } {
  const days = PRESET_DAYS[preset];
  if (days) {
    return { from: startOfDay(subDays(new Date(), days)), to: null };
  }
  if (preset === 'custom') {
    return { from: customFrom ?? null, to: customTo ?? null };
  }
  return { from: null, to: null };
}
